import React, { useState, useContext } from 'react';
import { Button, Alert, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useMutation, gql } from '@apollo/client';
import { AuthContext } from '../context/AuthContext';
import AuthShell from '../components/AuthShell';
import { FormTextField } from '../components/AuthFields';
import PasswordField from '../components/AuthFields';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import MailRoundedIcon from '@mui/icons-material/MailRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';

const CREATE_USER_MUTATION = gql`
  mutation CreateUser($username: String!, $email: String!, $password: String!) {
    createUser(username: $username, email: $email, password: $password) {
      user {
        id
        username
        email
      }
      success
    }
  }
`;

const TOKEN_AUTH_MUTATION = gql`
  mutation TokenAuth($username: String!, $password: String!) {
    tokenAuth(username: $username, password: $password) {
      token
      refreshToken
    }
  }
`;

const VALUE_BULLETS = [
  { icon: BoltRoundedIcon, text: 'Sincronización automática con tu banco' },
  { icon: CategoryRoundedIcon, text: 'Categorización táctil en segundos' },
  { icon: InsightsRoundedIcon, text: 'Dashboard visual de tus gastos' },
];

const Register = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [createUser, { loading }] = useMutation(CREATE_USER_MUTATION);
  const [tokenAuth] = useMutation(TOKEN_AUTH_MUTATION);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    try {
      const { data } = await createUser({
        variables: { username, email, password },
      });

      if (data.createUser.success) {
        const { data: authData } = await tokenAuth({
          variables: { username, password },
        });
        login(authData.tokenAuth.token, authData.tokenAuth.refreshToken, { username });
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.message || 'No se pudo crear la cuenta');
    }
  };

  return (
    <AuthShell
      overline="CREA TU CUENTA"
      title="Crea tu"
      titleAccent="cuenta"
      subtitle="Conecta tu banco y empieza a controlar tus gastos en menos de 2 minutos."
      bullets={VALUE_BULLETS}
      trustChip={{
        icon: <VerifiedUserRoundedIcon />,
        label: 'Información segura',
      }}
      footer={{
        text: '¿Ya tienes cuenta?',
        label: 'Iniciar sesión',
        to: '/login',
      }}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <FormTextField
            fullWidth
            label="Usuario"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            icon={PersonRoundedIcon}
            required
          />
          <FormTextField
            fullWidth
            label="Correo electrónico"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            icon={MailRoundedIcon}
            required
          />
          <PasswordField
            fullWidth
            label="Contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            icon={LockRoundedIcon}
            required
          />
          <PasswordField
            fullWidth
            label="Confirmar contraseña"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            icon={LockRoundedIcon}
            required
          />
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            size="large"
            type="submit"
            disabled={loading}
          >
            {loading ? 'Creando cuenta...' : 'Crear cuenta'}
          </Button>
        </Stack>
      </form>
    </AuthShell>
  );
};

export default Register;